import styled from 'styled-components';
import _ from 'lodash';
import SurveyResult from 'assets/Lotties/assessment_result.json';
import Lottie from 'react-lottie';
import React from 'react';
import { useBlocker, useLocation, useNavigate } from 'react-router-dom';
import useToggle from 'hooks/CustomHooks/useToggle';
import { useEffect, useRef, useState } from 'react';
import { mindUpAssessmentApi } from 'api/MindUp/mindUpAssessmentApi';
import { GetResultDiagnosisApiResponse } from 'models/DCK/DCKApiTypes';
import { VoucherCodeType } from 'utils/statusCode';
import { BackgroundColor, OpacityColor, TextColor } from 'styles/Global/colorSemantic';
import MobileLeaveModal from 'components/Modal/MobileLeaveModal';
import { connectMindPaths, preciousChildPaths } from 'constants/path';
import { mindupMicsApi } from 'api/MindUp/mindUpMicsApi';
import { OpenAiApi } from 'api/OpenAi/OpenAi';
import { AssessmentRequest } from 'models/MindUp/MindUpTypes';
import { mindUpDiagnosisApi } from 'api/MindUp/mindUpDiagnosisApi';
import { DCKDiagnosisApi } from 'api/DCK/DCKDiagnosisApi';
import { Body3Medium, Body5Medium } from 'styles/Global/typography';
import { StorageKeys } from 'constants/storageKeys';
import { useGovernmentHeaderStore } from 'store/useGovernmentHeaderStore';
import InpsytResults from './Components/InpsytResults';
import ResultNotiMsg from './Components/ResultNotiMsg';
import SectionTitle from './Components/SectionTitle';
import AIResultButton from './Components/AIResultButton';
import InspectorInfo from './Components/InspectorInfo';
import ResultPageTitle from './Components/ResultPageTitle';
import AIInspectorInfo from './Components/AiInspectorInfo';
import PaymentInfo from './Components/PaymentInfo';
import IcArrowOnlyDirection from 'assets/Icons/Arrow/IcArrowOnlyDirection';

// react-lottie 타입 이슈(React 18) 우회를 위한 래퍼
const LottieAny = Lottie as unknown as React.ComponentType<any>;

const ResultPage = () => {
    const { state } = useLocation();
    const [isLeaveModal, setIsLeaveModal] = useToggle();
    const [isShowMoveModal, setIsShowMoveModal] = useToggle();
    const [isShowAnalysisModal, setIsShowAnalysisModal] = useToggle();
    const [assessmentResult, setAssessmentResult] = useState<GetResultDiagnosisApiResponse>();
    const { setInit } = useGovernmentHeaderStore();
    const navigate = useNavigate();

    const diagnosisInfo = state.diagnosis;
    const token = state.token as string;
    const voucher = state.voucher;
    const payment = state.payment;
    const analysisLockRef = useRef<boolean>(false);

    const isAiVoucher = (voucher?.type ?? voucher?.data.voucher.type) === VoucherCodeType.MINDUPAI;
    const hasAiOnServer = !!diagnosisInfo?.assessmentAiResult;

    // ✅ diagnosis별 storage key
    const aiRawKey = `aiResultRaw:${diagnosisInfo._id}`;
    const aiDoneKey = `aiDone:${diagnosisInfo._id}`;

    const defaultOptions = {
        loop: true,
        autoplay: true,
        animationData: SurveyResult,
        rendererSettings: { preserveAspectRatio: 'xMidYMid slice' },
    };

    useBlocker(({ currentLocation, nextLocation, historyAction }) => {
        // aiAnalyze 페이지와의 이동은 허용
        if (nextLocation.pathname === preciousChildPaths.aiAnalyze || currentLocation.pathname === preciousChildPaths.aiAnalyze) {
            return false;
        }

        // 뒤로가기만 차단 (forward/push는 허용)
        if (historyAction === 'POP') {
            setIsShowMoveModal();
            return true;
        }
        return false;
    });

    useEffect(() => {
        setInit();
    }, []);

    useEffect(() => {
        (async () => {
            const res = await mindUpAssessmentApi.getAssessmentResultByDiagnosisId(diagnosisInfo._id, token);
            setAssessmentResult(res);
        })();

        if (isAiVoucher && hasAiOnServer) {
            sessionStorage.setItem(aiDoneKey, 'true');

            if (!sessionStorage.getItem(aiRawKey)) {
                sessionStorage.setItem(aiRawKey, JSON.stringify(diagnosisInfo.assessmentAiResult));
            }
        }
    }, []);

    useEffect(() => {
        if (!isAiVoucher) return;

        const alreadyDone = sessionStorage.getItem(aiDoneKey) === 'true';
        if (alreadyDone) return;
        if (!assessmentResult?.data?.length) return; // 원본이 준비되어야 분석 가능
        if (analysisLockRef.current) return;

        analysisLockRef.current = true;

        (async () => {
            try {
                const code = voucher?.code ?? voucher?.data.voucher.code;
                const saved = await analyzeAndSaveResult(code as string, diagnosisInfo._id, token, assessmentResult);

                // analyzeAndSaveResult 내부에서 cleaned 저장 + 서버 저장까지 마침
                if (saved?.data?.assessmentAiResult) {
                    sessionStorage.setItem(aiDoneKey, 'true');
                }
            } catch (err) {
                console.error('❌ AI 분석/저장 실패:', err);
            }
        })();
    }, [assessmentResult, diagnosisInfo._id, isAiVoucher, token, voucher]);

    /** 3) 버튼: 완료면 이동, 아니면 모달 */
    const handleOpenAiResult = async () => {
        const isDone = sessionStorage.getItem(aiDoneKey) === 'true';
        if (!isDone) {
            setIsShowAnalysisModal();
            return;
        }
        const raw = sessionStorage.getItem(aiRawKey);
        const fetchedDiagnosis = await DCKDiagnosisApi.getDiagnosis(diagnosisInfo._id, token);

        if (fetchedDiagnosis.data.assessmentAiResult) {
            console.log('aiResult:', fetchedDiagnosis.data.assessmentAiResult);
            // raw는 cleaned 문자열이거나(분석 직후) 서버 객체의 JSON.stringify(초기 동기화 케이스)
            navigate(preciousChildPaths.aiAnalyze, {
                replace: false,
                state: {
                    result: JSON.parse(raw!),
                    data: { diagnosisInfo: fetchedDiagnosis.data, voucher: voucher, token: token },
                    testRawData: JSON.stringify(
                        assessmentResult?.data?.map((item: any) => ({
                            ozUrl: item.ozUrl,
                            code: item.code,
                            PSY_ITEM_ID: item.code,
                            resultData: {
                                ozJsonResultData: item.ozJsonResultData,
                                ozJsonInfoData: item.ozJsonInfoData,
                            },
                            message: '채점에 성공하였습니다.',
                            psyOnlineCode: item.psyOnlineCode,
                            ...item, // 원본 데이터 포함
                        })) || [],
                        null,
                        2
                    ),
                },
            });
        }
    };

    /** 4) 분석 + 저장
     * - cleaned 문자열을 반드시 sessionStorage(aiResultRaw:<id>)에 저장
     * - 파싱 성공 시 서버 저장 후 반환
     */
    const analyzeAndSaveResult = async (voucherCode: string, diagnosisId: string, token: string, assessmentResult: GetResultDiagnosisApiResponse | undefined) => {
        if (!assessmentResult) return;

        const aiResponse = await mindupMicsApi.verifyAiAccess(voucherCode, diagnosisId, token);

        const rawData = JSON.stringify(
            assessmentResult.data.map((item: any) => ({
                ozUrl: item.ozUrl,
                code: item.code,
                resultData: {
                    ozJsonResultData: item.ozJsonResultData,
                    ozJsonInfoData: item.ozJsonInfoData,
                },
                message: '채점에 성공하였습니다.',
                psyOnlineCode: item.psyOnlineCode,
            })),
            null,
            2
        );

        const openAiApiInstance = new OpenAiApi(aiResponse.data.apiKey);
        await openAiApiInstance.initAssistant(aiResponse.data.model, 'vs_6879fb609a908191afee146d062d61d7');

        const res = await openAiApiInstance.responseAiResult(rawData, { useVectorStore: true });
        const cleaned = res
            .replace(/^```json\s*/, '')
            .replace(/```$/, '')
            .trim();

        // ✅ 핵심: cleaned를 "그대로" 저장 (aiResult = cleaned)
        sessionStorage.setItem(aiRawKey, cleaned);

        // 서버 저장은 파싱 성공 시에만 진행 (UI는 cleaned로 이동 가능)
        let parsed: any;
        try {
            parsed = JSON.parse(cleaned || '{}');
        } catch (e) {
            console.error('❌ JSON parse 실패', e, cleaned);
            // 파싱 실패해도 raw는 세션에 남아있으므로, 사용자에게 재시도/오류 안내 가능
            throw e;
        }

        // 최소 구조 검증 (필요 시 완화/제거 가능)

        if (_.isEmpty(parsed.totalResult) || _.isEmpty(parsed.summaryList) || _.isEmpty(parsed.recommendList)) {
            // 임시로 검증 완화
            console.warn('⚠️ AI 분석 결과 일부 필드가 비어있지만 진행합니다.');
        }

        const assessmentRequest: AssessmentRequest = {
            diagnosisId,
            assessmentAiResult: parsed, // 서버에는 객체로 저장
        };

        return await mindUpDiagnosisApi.updateAssessmentAiResult(assessmentRequest, token);
    };

    const renderAiResult = () => (
        <>
            <ResultPageTitle description='결과와 AI의 분석을 확인해보세요' />
            <ContentContainer $isAiVoucher={isAiVoucher}>
                <SectionContainer>
                    <SectionTitle leftTitle='검사 대상자' />
                    <AIInspectorInfo diagnosisInfo={diagnosisInfo} />
                </SectionContainer>
                <SectionContainer>
                    <SectionTitle leftTitle='결과 분석' />
                    <AIResultButton onClick={handleOpenAiResult} />
                </SectionContainer>
                <SectionContainer>
                    <SectionTitle leftTitle='검사 결과' rightTitle={`${assessmentResult?.data.length ?? 0}`} />
                    <ResultNotiMsg />
                    <InpsytResults diagnosisInfo={diagnosisInfo} assessmentResult={assessmentResult} />
                </SectionContainer>
                {diagnosisInfo?.paymentId && (
                    <SectionContainer>
                        <SectionTitle leftTitle='결제 정보' />
                        <PaymentInfo paymentInfo={payment} />
                    </SectionContainer>
                )}
                <PreviousDiagnosisButton
                    onClick={() => {
                        const encryptedKey = sessionStorage.getItem(StorageKeys.ENCRYPTED_DEVICE_KEY) ?? '';
                        mindUpDiagnosisApi
                            .getListByDeviceKey(encryptedKey)
                            .then((res) => {
                                navigate(connectMindPaths.sungnamDiagnosisHistoryList, { replace: false, state: { diagnosisList: res.data.list, token: token } });
                            })
                            .catch((error) => {
                                navigate(connectMindPaths.sungnamError, {
                                    replace: false,
                                    state: { errorCode: error.code },
                                });
                            });
                    }}
                >
                    <Body3Medium>검사 목록보기</Body3Medium>
                </PreviousDiagnosisButton>
                <CallButtonArea>
                    <CallButton
                        onClick={() => {
                            window.location.href = 'tel:031-705-1426';
                        }}
                    >
                        <Body5Medium $color={TextColor.color_text_light}>문의 사항이 있으신가요?</Body5Medium>
                        <IcArrowOnlyDirection />
                    </CallButton>
                </CallButtonArea>
            </ContentContainer>
        </>
    );

    const renderBasicResult = () => (
        <>
            <ResultPageTitle title='검사 결과를 확인해보세요!' />
            <ContentContainer $gap='16px'>
                <ResultNotiMsg />
                <InspectorInfo diagnosisInfo={diagnosisInfo} />
                <InpsytResults diagnosisInfo={diagnosisInfo} assessmentResult={assessmentResult} />
            </ContentContainer>
        </>
    );
    return (
        <PageLayout>
            {isLeaveModal && (
                <MobileLeaveModal
                    title={'검사가 이미 완료되어'}
                    subTitle='접근할 수 없습니다.'
                    content={`검사가 완료되어 이전 검사지로 다시 접근할 수 없습니다.`}
                    firstButton='확인'
                    firstButtonType='fill'
                    infoContentsPadding='0px'
                    infoContentsBorderColor={BackgroundColor.color_background_primary_neutral}
                    infoContentsBorderRadius='8px'
                    setIsLeaveModal={setIsLeaveModal}
                />
            )}
            {isShowMoveModal && isAiVoucher && (
                <MobileLeaveModal
                    title={'검사 목록으로 이동합니다.'}
                    content={`검사 목록으로 이동하시겠습니까?`}
                    firstButton='아니요'
                    firstButtonType='gray'
                    secondButton='이동하기'
                    secondButtonType='fill'
                    infoContentsPadding='0px'
                    infoContentsBorderColor={BackgroundColor.color_background_primary_neutral}
                    infoContentsBorderRadius='8px'
                    setIsLeaveModal={setIsShowMoveModal}
                    clickLeaveButtonInLeaveModal={async () => {
                        const encryptedKey = sessionStorage.getItem(StorageKeys.ENCRYPTED_DEVICE_KEY) ?? '';
                        const historyInfo = await mindUpDiagnosisApi.getListByDeviceKey(encryptedKey);
                        navigate(connectMindPaths.sungnamDiagnosisHistoryList, { replace: false, state: { diagnosisList: historyInfo.data.list, token: historyInfo.data.token } });
                    }}
                />
            )}
            {isShowAnalysisModal && (
                <MobileLeaveModal
                    title='AI가 분석 중이에요'
                    content={`분석 완료까지는 일정시간이 소요되어요.\n잠시 후, 다시 시도해 주세요.`}
                    firstButton='확인'
                    firstButtonType='fill'
                    infoContentsPadding='0px'
                    infoContentsBorderColor={BackgroundColor.color_background_primary_neutral}
                    infoContentsBorderRadius='8px'
                    setIsLeaveModal={setIsShowAnalysisModal}
                />
            )}
            <LottieWrapper>
                <LottieAny options={defaultOptions} width={140} height={140} />
            </LottieWrapper>
            <ExplainContainer>{isAiVoucher ? renderAiResult() : renderBasicResult()}</ExplainContainer>
        </PageLayout>
    );
};

export default ResultPage;

const PageLayout = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
    height: calc(100vh - 104px);
    width: 100%;
`;

const ContentContainer = styled.div<{ $isAiVoucher?: boolean; $gap?: string }>`
    display: flex;
    flex-direction: column;
    gap: ${(props) => props.$gap || '0px'};
    padding: 0 16px;
    background-color: ${(props) => (props.$isAiVoucher ? BackgroundColor.color_background_light : 'transparent')};
`;

const ExplainContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 20px;
`;

const LottieWrapper = styled.div`
    padding-bottom: 8px;
`;

const SectionContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    margin-top: 24px;
    gap: 10px;
`;

const PreviousDiagnosisButton = styled.div`
    width: 100%;
    margin-top: 10px;
    margin-bottom: 30px;
    padding: 16px 0px;
    border-radius: 8px;
    background-color: ${OpacityColor.color_opacity_primary_light_8};
    display: flex;
    justify-content: center;
    align-items: center;
`;

const CallButtonArea = styled.div`
    display: flex;
    justify-content: center;
`;

const CallButton = styled.div`
    display: flex;
    align-items: center;
    background-color: ${OpacityColor.color_opacity_neutral_8};
    padding: 12px 12px 12px 20px;
    border-radius: 999px;
    gap: 2px;
    margin-bottom: 52px;
`;
